import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchMasterLists, MasterListItem } from "../../apis";

/**
 * Where the one master-list read stands. `idle` is a read-only view, which never starts it: there is
 * nothing to pick when nothing can be changed.
 */
export type MasterListStatus = "idle" | "loading" | "ready" | "failed";

export interface MasterLists {
  status: MasterListStatus;
  /** The `role` slug's options, `{ Id, Name }` — the ids a job stores. Empty until `ready`. */
  roles: MasterListItem[];
  /** The `city` slug's names. Location has no id to send, so only the names survive here. */
  locations: string[];
  /** Reads the list again, leaving every answer already filled in untouched. */
  retry: () => void;
}

/**
 * Role and Location (or just Location, with `requireRoles: false` for a form that has no Role picker), read once for the whole form.
 *
 * One request for both slugs (`fetchMasterLists`), held here for as long as the layout is mounted:
 * the lists are static for a session, so neither field fetches per keystroke and neither fetches on
 * its own. Both fields read this one state, so one failure is reported once and one retry fixes both.
 *
 * `retry` re-runs the read by bumping an attempt counter rather than by clearing a cache, so nothing
 * the learner has typed or picked is remounted or reset — the Form keeps its values throughout.
 */
export const useMasterLists = (enabled: boolean, { requireRoles = true }: { requireRoles?: boolean } = {}): MasterLists => {
  const [status, setStatus] = useState<MasterListStatus>("idle");
  const [roles, setRoles] = useState<MasterListItem[]>([]);
  const [cities, setCities] = useState<MasterListItem[]>([]);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setStatus("loading");
    fetchMasterLists({ requireRoles })
      .then(lists => {
        if (!active) return;
        setRoles(lists.role);
        setCities(lists.city);
        setStatus("ready");
      })
      .catch(() => {
        // Nothing partial to keep: the read is rejected unless it carried roles. Both fields say so.
        if (!active) return;
        setRoles([]);
        setCities([]);
        setStatus("failed");
      });
    return () => {
      active = false;
    };
  }, [enabled, attempt, requireRoles]);

  const locations = useMemo(() => cities.map(({ Name }) => Name), [cities]);
  const retry = useCallback(() => setAttempt(current => current + 1), []);

  return { status, roles, locations, retry };
};
